import {
  TEntityResponse,
  TEntityListResponse,
  TCreateSubgroupReqBody,
  TSubgroupResBody,
  TSubgroupSummaryResBody,
  TUpdateSubgroupReqBody,
  TDeviceSummaryResBody,
  TMulticastGroupActionSummaryResBody,
} from '@poseidon-city/shared';

import { TAPIClient } from '../client';

type TMulticastGroupsClient = {
  create(body: TCreateSubgroupReqBody): Promise<TEntityResponse<'subgroup', TSubgroupResBody> | undefined>;
  update(id: string, body: TUpdateSubgroupReqBody): Promise<TEntityResponse<'subgroup', TSubgroupResBody> | undefined>;
  delete(id: string): Promise<void>;
  devices(id: string): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined>;
  get(id: string): Promise<TEntityResponse<'subgroup', TSubgroupResBody> | undefined>;
  list(seletectedAreaId: string): Promise<TEntityListResponse<'subgroup', TSubgroupSummaryResBody> | undefined>;
  list_filtered(
    limit: number,
    page: number,
    type: 'all' | 'with-plan' | 'without-plan',
    filter: 'area' | 'company',
    id: string
  ): Promise<TEntityListResponse<'subgroup', TSubgroupSummaryResBody> | undefined>;
  list_all(
    limit: number,
    page: number,
    type: 'all' | 'with-plan' | 'without-plan'
  ): Promise<TEntityListResponse<'subgroup', TSubgroupSummaryResBody> | undefined>;
  get_available_commands(id: string): Promise<TEntityListResponse<'subgroup-command', any> | undefined>;
  execute_command(id: string, command: any): Promise<void>;
  add_device(subgroupId: string, deviceId: string): Promise<void>;
  add_devices(subgroupId: string, deviceIds: string[]): Promise<void>;
  actions_log(
    mcGroupId: string,
    limit: number,
    page: number
  ): Promise<TEntityListResponse<'multicast-group-action', TMulticastGroupActionSummaryResBody> | undefined>;
};

function MulticastGroupsClient(apiClient: TAPIClient): TMulticastGroupsClient {
  return {
    async create(body: TCreateSubgroupReqBody): Promise<TEntityResponse<'subgroup', TSubgroupResBody> | undefined> {
      const data = await apiClient.post<TCreateSubgroupReqBody, TEntityResponse<'subgroup', TSubgroupResBody>>(
        `/subgroups`,
        body
      );

      if (data && data.type !== 'subgroup') {
        throw new Error('Invalid entity type received, expected: subgroup, received: ' + data.type);
      }

      return data;
    },

    async update(
      id: string,
      body: TUpdateSubgroupReqBody
    ): Promise<TEntityResponse<'subgroup', TSubgroupResBody> | undefined> {
      const data = await apiClient.put<TUpdateSubgroupReqBody, TEntityResponse<'subgroup', TSubgroupResBody>>(
        `/subgroups/${id}`,
        body
      );

      if (data && data.type !== 'subgroup') {
        throw new Error('Invalid entity type received, expected: subgroup, received: ' + data.type);
      }

      return data;
    },

    async delete(id: string): Promise<void> {
      return await apiClient.delete(`/subgroups/${id}`);
    },

    async devices(id: string): Promise<TEntityListResponse<'device', TDeviceSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'device', TDeviceSummaryResBody>>(
        `/subgroups/${id}/devices`
      );

      if (data && data.type !== 'device') {
        throw new Error('Invalid entity type received, expected: device, received: ' + data.type);
      }

      return data;
    },

    async get(id: string): Promise<TEntityResponse<'subgroup', TSubgroupResBody> | undefined> {
      const data = await apiClient.get<TEntityResponse<'subgroup', TSubgroupResBody>>(`/subgroups/${id}`);

      if (data && data.type !== 'subgroup') {
        throw new Error('Invalid entity type received, expected: subgroup, received: ' + data.type);
      }

      return data;
    },

    async list(selectedAreaId: string): Promise<TEntityListResponse<'subgroup', TSubgroupSummaryResBody> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'subgroup', TSubgroupSummaryResBody>>(
        `/areas/${selectedAreaId}/subgroups`
      );

      if (data && data.type !== 'subgroup') {
        throw new Error('Invalid entity type received, expected: subgroup, received: ' + data.type);
      }

      return data;
    },

    async list_filtered(
      limit: number,
      page: number,
      type: 'all' | 'with-plan' | 'without-plan',
      filter: 'area' | 'company',
      id: string
    ): Promise<TEntityListResponse<'subgroup', TSubgroupSummaryResBody> | undefined> {
      const selectedFilter = filter === 'area' ? 'area_id' : 'company_id';

      let data;
      if (type === 'with-plan') {
        data = await apiClient.get<TEntityListResponse<'subgroup', TSubgroupSummaryResBody>>(
          `/subgroups?filter[]=with-plan&filter[${selectedFilter}]=${id}&limit=${limit}&page=${page}`
        );
      } else if (type === 'without-plan') {
        data = await apiClient.get<TEntityListResponse<'subgroup', TSubgroupSummaryResBody>>(
          `/subgroups?filter[]=without-plan&filter[${selectedFilter}]=${id}&limit=${limit}&page=${page}`
        );
      } else {
        data = await apiClient.get<TEntityListResponse<'subgroup', TSubgroupSummaryResBody>>(
          `/subgroups?filter[${selectedFilter}]=${id}&limit=${limit}&page=${page}`
        );
      }

      if (data && data.type !== 'subgroup') {
        throw new Error('Invalid entity type received, expected: subgroup, received: ' + data.type);
      }

      return data;
    },

    async list_all(
      limit: number,
      page: number,
      type: 'all' | 'with-plan' | 'without-plan'
    ): Promise<TEntityListResponse<'subgroup', TSubgroupSummaryResBody> | undefined> {
      let data;
      if (type === 'with-plan') {
        data = await apiClient.get<TEntityListResponse<'subgroup', TSubgroupSummaryResBody>>(
          `/subgroups?filter[]=with-plan&limit=${limit}&page=${page}`
        );
      } else if (type === 'without-plan') {
        data = await apiClient.get<TEntityListResponse<'subgroup', TSubgroupSummaryResBody>>(
          `/subgroups?filter[]=without-plan&limit=${limit}&page=${page}`
        );
      } else {
        data = await apiClient.get<TEntityListResponse<'subgroup', TSubgroupSummaryResBody>>(
          `/subgroups?limit=${limit}&page=${page}`
        );
      }

      if (data && data.type !== 'subgroup') {
        throw new Error('Invalid entity type received, expected: subgroup, received: ' + data.type);
      }

      return data;
    },

    async get_available_commands(id: string): Promise<TEntityListResponse<'subgroup-command', any> | undefined> {
      const data = await apiClient.get<TEntityListResponse<'subgroup-command', any>>(`/subgroups/${id}/commands`);

      if (data && data.type !== 'subgroup-command') {
        throw new Error('Invalid entity type received, expected: subgroup-command, received: ' + data.type);
      }

      return data;
    },

    async execute_command(id: string, command: any): Promise<void> {
      return apiClient.post(`/subgroups/${id}/commands`, command);
    },

    async add_device(subgroupId: string, deviceId: string): Promise<void> {
      return apiClient.post(`/subgroups/${subgroupId}/devices`, {
        device_ids: [deviceId],
      });
    },

    async add_devices(subgroupId: string, deviceIds: string[]): Promise<void> {
      return apiClient.post(`/subgroups/${subgroupId}/devices`, {
        device_ids: deviceIds,
      });
    },

    async actions_log(
      mcGroupId: string,
      limit: number,
      page: number
    ): Promise<TEntityListResponse<'multicast-group-action', TMulticastGroupActionSummaryResBody> | undefined> {
      const data = await apiClient.get<
        TEntityListResponse<'multicast-group-action', TMulticastGroupActionSummaryResBody>
      >(
        `/multicast-group-actions?sort[created_at]=desc&filter[multicast_group_id]=${mcGroupId}&limit=${limit}&page=${page}`
      );

      if (data && data.type !== 'multicast-group-action') {
        throw new Error('Invalid entity type received, expected: multicast-group-action, received: ' + data.type);
      }

      return data;
    },
  };
}

export { TMulticastGroupsClient, MulticastGroupsClient };
